import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import * as THREE from 'three'

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

interface AnimatedBirdsProps {
  count?: number
  color?: string
  size?: number
}

const AnimatedBirds: React.FC<AnimatedBirdsProps> = ({
  count = 40,
  color = '#ffffff',
  size = 2.4,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mouseRef = useRef(new THREE.Vector3())
  const animationFrameRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const width = container.offsetWidth
    const height = container.offsetHeight
    const halfWidth = width / 2
    const halfHeight = height / 2

    mouseRef.current.set(0, 0, 0)

    class Bird extends THREE.BufferGeometry {
      constructor() {
        super()

        const vertices = new Float32Array([
          5, 0, 0, -5, -2, 1, -5, 0, 0, -5, -2, -1, 0, 2, -6, 0, 2, 6, 2, 0, 0,
          -3, 0, 0,
        ])

        const indices = new Uint16Array([0, 2, 1, 4, 7, 6, 5, 6, 7])

        this.setAttribute('position', new THREE.BufferAttribute(vertices, 3))
        this.setIndex(new THREE.BufferAttribute(indices, 1))
      }
    }

    class Flock {
      position: THREE.Vector3
      velocity: THREE.Vector3
      acceleration: THREE.Vector3
      neighborhoodRadius = 200
      maxSpeed = 5
      maxSteerForce = 0.1
      avoidWalls = false
      worldWidth = 500
      worldHeight = 500
      worldDepth = 200
      goal?: THREE.Vector3

      constructor() {
        this.position = new THREE.Vector3()
        this.velocity = new THREE.Vector3()
        this.acceleration = new THREE.Vector3()
      }

      setGoal(target: THREE.Vector3) {
        this.goal = target
      }

      setAvoidWalls(value: boolean) {
        this.avoidWalls = value
      }

      setWorldSize(width: number, height: number, depth: number) {
        this.worldWidth = width
        this.worldHeight = height
        this.worldDepth = depth
      }

      run(flocks: Flock[]) {
        this.flock(flocks)
        this.move()
      }

      flock(flocks: Flock[]) {
        if (this.goal) {
          this.acceleration.add(this.reach(this.goal, 0.005))
        }
        this.acceleration.add(this.alignment(flocks))
        this.acceleration.add(this.cohesion(flocks))
        this.acceleration.add(this.separation(flocks))
      }

      move() {
        this.velocity.add(this.acceleration)
        const l = this.velocity.length()
        if (l > this.maxSpeed) {
          this.velocity.divideScalar(l / this.maxSpeed)
        }
        this.position.add(this.velocity)
        this.acceleration.set(0, 0, 0)
      }

      checkBounds() {
        if (this.position.x > this.worldWidth)
          this.position.x = -this.worldWidth
        if (this.position.x < -this.worldWidth)
          this.position.x = this.worldWidth
        if (this.position.y > this.worldHeight)
          this.position.y = -this.worldHeight
        if (this.position.y < -this.worldHeight)
          this.position.y = this.worldHeight
        if (this.position.z > this.worldDepth)
          this.position.z = -this.worldDepth
        if (this.position.z < -this.worldDepth)
          this.position.z = this.worldDepth
      }

      repulse(target: THREE.Vector3) {
        const distance = this.position.distanceTo(target)
        if (distance < 150) {
          const steer = new THREE.Vector3()
          steer.subVectors(this.position, target)
          steer.multiplyScalar(0.1 / distance)
          this.acceleration.add(steer)
        }
      }

      reach(target: THREE.Vector3, amount: number) {
        const steer = new THREE.Vector3()
        steer.subVectors(target, this.position)
        steer.multiplyScalar(amount)
        return steer
      }

      alignment(flocks: Flock[]) {
        const velSum = new THREE.Vector3()
        let count = 0

        for (let i = 0; i < flocks.length; i++) {
          if (Math.random() > 0.6) continue
          const other = flocks[i]
          const distance = other.position.distanceTo(this.position)
          if (distance > 0 && distance <= this.neighborhoodRadius) {
            velSum.add(other.velocity)
            count++
          }
        }

        if (count > 0) {
          velSum.divideScalar(count)
          const l = velSum.length()
          if (l > this.maxSteerForce) {
            velSum.divideScalar(l / this.maxSteerForce)
          }
        }

        return velSum
      }

      cohesion(flocks: Flock[]) {
        const posSum = new THREE.Vector3()
        const steer = new THREE.Vector3()
        let count = 0

        for (let i = 0; i < flocks.length; i++) {
          if (Math.random() > 0.6) continue
          const other = flocks[i]
          const distance = other.position.distanceTo(this.position)
          if (distance > 0 && distance <= this.neighborhoodRadius) {
            posSum.add(other.position)
            count++
          }
        }

        if (count > 0) {
          posSum.divideScalar(count)
        }

        steer.subVectors(posSum, this.position)
        const l = steer.length()
        if (l > this.maxSteerForce) {
          steer.divideScalar(l / this.maxSteerForce)
        }

        return steer
      }

      separation(flocks: Flock[]) {
        const posSum = new THREE.Vector3()

        for (let i = 0; i < flocks.length; i++) {
          if (Math.random() > 0.6) continue
          const other = flocks[i]
          const distance = other.position.distanceTo(this.position)
          if (distance > 0 && distance <= this.neighborhoodRadius) {
            const repulse = new THREE.Vector3()
            repulse.subVectors(this.position, other.position)
            repulse.normalize()
            repulse.divideScalar(distance)
            posSum.add(repulse)
          }
        }

        return posSum
      }
    }

    const camera = new THREE.PerspectiveCamera(75, width / height, 1, 10000)
    camera.position.z = 450

    const scene = new THREE.Scene()

    const birds: THREE.Mesh[] = []
    const flocks: Flock[] = []
    const birdOffsets: THREE.Vector3[] = []
    const birdZPositions: number[] = []

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const radius = Math.sqrt(Math.random()) * Math.min(width, height) * 0.4
      const randomX = Math.cos(angle) * radius
      const randomY = Math.sin(angle) * radius

      const flock = new Flock()
      flock.position.x = randomX
      flock.position.y = randomY
      flock.position.z = 0
      flock.velocity.x = Math.random() * 2 - 1
      flock.velocity.y = Math.random() * 2 - 1
      flock.velocity.z = 0
      flock.setAvoidWalls(true)
      flock.setWorldSize(width / 2, height / 2, 1)
      flocks.push(flock)

      const offsetAngle = Math.random() * Math.PI * 2
      const offsetRadius = Math.sqrt(Math.random()) * 100
      birdOffsets.push(
        new THREE.Vector3(
          Math.cos(offsetAngle) * offsetRadius,
          Math.sin(offsetAngle) * offsetRadius,
          0
        )
      )

      const birdZ = (Math.random() - 0.5) * 60
      birdZPositions.push(birdZ)

      const birdSize = size

      const bird = new THREE.Mesh(
        new Bird(),
        new THREE.MeshBasicMaterial({
          color: color,
          side: THREE.DoubleSide,
        })
      )
      bird.userData.phase = Math.floor(Math.random() * 62.83)
      bird.position.copy(flock.position)
      bird.position.z = birdZ
      bird.scale.multiplyScalar(birdSize)
      scene.add(bird)
      birds.push(bird)
    }

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setSize(width, height)
    container.appendChild(renderer.domElement)

    const handleMouseMove = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const x = Math.max(
        -halfWidth,
        Math.min(halfWidth, event.clientX - rect.left - halfWidth)
      )
      const y = Math.max(
        -halfHeight,
        Math.min(halfHeight, -(event.clientY - rect.top) + halfHeight)
      )
      mouseRef.current.set(x, y, 0)
    }
    window.addEventListener('mousemove', handleMouseMove)

    const animate = () => {
      for (let i = 0; i < birds.length; i++) {
        const flock = flocks[i]

        const goal = mouseRef.current.clone().add(birdOffsets[i])
        goal.z = 0
        flock.setGoal(goal)

        flock.run(flocks)

        flock.position.z = 0

        const bird = birds[i]
        bird.position.copy(flock.position)
        bird.position.z = birdZPositions[i]
        bird.rotation.y = Math.atan2(-flock.velocity.z, flock.velocity.x)
        bird.rotation.z = Math.asin(flock.velocity.y / flock.velocity.length())

        bird.userData.phase =
          (bird.userData.phase + (Math.max(0, bird.rotation.z) + 0.1)) % 62.83

        const geometry = bird.geometry as THREE.BufferGeometry
        const positions = geometry.attributes.position as THREE.BufferAttribute
        positions.setY(4, Math.sin(bird.userData.phase) * 5)
        positions.setY(5, Math.sin(bird.userData.phase) * 5)
        positions.needsUpdate = true
      }

      renderer.render(scene, camera)
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      renderer.dispose()
      container.removeChild(renderer.domElement)
    }
  }, [count, color, size])

  return <Container ref={containerRef} />
}

export default AnimatedBirds
